// Copyright (c) 2026 PGNS LLC

/**
 * Client for the public status API served by `apps/status-worker/`.
 *
 * The shapes here mirror `apps/status-worker/src/api.ts`, which is a documented
 * product contract (additive changes only) — so this file parses, it does not
 * negotiate. Every call is bounded by a timeout: a status page that hangs on a
 * dead API is indistinguishable from a status page that is itself down.
 */

import { STATUS_API_URL } from './env';

export type ComponentStatus = 'up' | 'degraded' | 'down' | 'unknown';

export interface SummaryComponent {
  id: string;
  name: string;
  description: string;
  kind: string;
  position: number;
  status: ComponentStatus;
  /** Last observation. Advances on every poll, even when nothing changed. */
  updated_at: string | null;
  /** Last actual status transition. Null until the component has ever changed. */
  changed_at?: string | null;
  availability_90d: number | null;
  up_seconds_90d: number;
  degraded_seconds_90d: number;
  down_seconds_90d: number;
}

export interface IncidentUpdate {
  ts: string;
  status: string;
  body_md: string;
  author: string;
}

export interface Incident {
  id: string;
  title: string;
  status: string;
  impact: string;
  started_at: string;
  resolved_at: string | null;
  resolved: boolean;
  components: string[];
  auto: boolean;
  updates: IncidentUpdate[];
}

export interface Summary {
  generated_at: string;
  status: ComponentStatus;
  description: string;
  window: { days: number; start_day: string; end_day: string };
  components: SummaryComponent[];
  active_incidents: Incident[];
}

export interface HistoryDay {
  day: string;
  status: ComponentStatus;
  availability: number | null;
  up_seconds: number;
  degraded_seconds: number;
  down_seconds: number;
  p50_ms: number | null;
  p95_ms: number | null;
  samples: number;
}

export interface HistoryComponent {
  id: string;
  name: string;
  kind: string;
  days: HistoryDay[];
}

export interface History {
  generated_at: string;
  days: number;
  start_day: string;
  end_day: string;
  components: HistoryComponent[];
}

export interface IncidentList {
  generated_at: string;
  incidents: Incident[];
}

/** Long enough to ride out a slow edge cache miss, short enough to fail visibly. */
const REQUEST_TIMEOUT_MS = 8000;

export const HISTORY_DAYS = 90;

/** The Atom feed lives on the API origin, which in production is this origin. */
export const FEED_URL = `${STATUS_API_URL}/feed.xml`;

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const response = await fetch(`${STATUS_API_URL}${path}`, {
    headers: { accept: 'application/json' },
    signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
  });
  if (!response.ok) throw new Error(`status API ${path} responded ${response.status}`);
  return (await response.json()) as T;
}

export function fetchSummary(signal?: AbortSignal): Promise<Summary> {
  return getJson<Summary>('/v1/summary', signal);
}

export function fetchHistory(days = HISTORY_DAYS, signal?: AbortSignal): Promise<History> {
  return getJson<History>(`/v1/history?days=${days}`, signal);
}

export function fetchIncidents(limit = 50, signal?: AbortSignal): Promise<IncidentList> {
  return getJson<IncidentList>(`/v1/incidents?status=all&limit=${limit}`, signal);
}
